/**
 * Stats Logger Utility
 *
 * Consolidates response statistics logging for AI responses.
 * Eliminates code duplication between onFinalResult and onComplete callbacks.
 */

interface UsageStats {
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
  word_count?: number;
  elapsed_time_seconds?: number;
  model_used?: string;
  cost_usd?: number;
  cost_clp?: number;
}

interface AIFlowStage {
  stage: string;
  name: string;
  time_ms: number;
  status: string;
  data?: Record<string, unknown>;
}

interface RAGStats {
  [key: string]: unknown;
}

export interface StatsLogData {
  usageStats?: UsageStats;
  aiFlowTrace?: AIFlowStage[];
  ragStats?: RAGStats;
  isMapReduce?: boolean;
}

/**
 * Format cost with appropriate precision
 */
export function formatCost(cost: number | undefined | null): string {
  if (cost === undefined || cost === null || isNaN(cost)) {
    return '$0.0000';
  }
  if (cost < 0.0001 && cost > 0) {
    return `$${cost.toFixed(6)}`;
  }
  return `$${cost.toFixed(4)}`;
}

/**
 * Log response statistics to console in a formatted table.
 * Only runs in development to avoid console noise in production.
 */
export function logResponseStats(data: StatsLogData): void {
  if (!import.meta.env.DEV) return;

  const { usageStats, aiFlowTrace, ragStats, isMapReduce = false } = data;

  console.log('\n\n');
  console.log('═══════════════════════════════════════════════════════════════');
  console.log(`📊 ESTADÍSTICAS DE RESPUESTA IA${isMapReduce ? ' (Map-Reduce)' : ''}`);
  console.log('═══════════════════════════════════════════════════════════════');

  if (usageStats) {
    console.log('\n💰 COSTOS Y TOKENS:');
    console.table({
      'Tokens Entrada': usageStats.prompt_tokens,
      'Tokens Salida': usageStats.completion_tokens,
      'Total Tokens': usageStats.total_tokens,
      Palabras: usageStats.word_count,
      'Tiempo (s)': usageStats.elapsed_time_seconds?.toFixed(2),
      Modelo: usageStats.model_used,
      'Costo USD': formatCost(usageStats.cost_usd),
      'Costo CLP': `$${usageStats.cost_clp?.toFixed(0)}`,
    });
  }

  if (aiFlowTrace && aiFlowTrace.length > 0) {
    console.log('\n🔄 FLUJO DEL SISTEMA IA:');
    const flowTable = aiFlowTrace.map((stage, idx) => ({
      '#': idx + 1,
      Etapa: stage.stage,
      Nombre: stage.name,
      'Tiempo (ms)': stage.time_ms,
      Estado: stage.status,
      Datos: stage.data ? JSON.stringify(stage.data).substring(0, 50) + '...' : '-',
    }));
    console.table(flowTable);

    console.log('\n📋 DETALLES COMPLETOS DE CADA ETAPA:');
    aiFlowTrace.forEach((stage, idx) => {
      console.log(`\n${idx + 1}. ${stage.name} (${stage.stage})`);
      console.log(`   ⏱️  Tiempo: ${stage.time_ms}ms`);
      console.log(`   ✅ Estado: ${stage.status}`);
      if (stage.data) {
        console.log('   📦 Datos:', stage.data);
      }
    });
  }

  if (ragStats) {
    console.log('\n🔍 ESTADÍSTICAS RAG:');
    console.table(ragStats);
  }

  console.log('\n═══════════════════════════════════════════════════════════════');
  console.log('\n\n');
}
